import cx from "classnames"
import { AlertCircle, ChevronDown, ChevronRight, Loader, Wrench } from "lucide-react"
import { useMemo, useState } from "react"
import type { CommentContext, GroupWithMeta } from "../events/messageGroups"
import { type ActivityDigestItem, buildActivityDigest, isActivityDigestItem } from "./activityDigestModel"
import { getRenderer } from "./renderers"

interface ActivityDigestProps {
    items: GroupWithMeta[]
    commentContext: CommentContext
}

export function ActivityDigest({ items, commentContext }: ActivityDigestProps) {
    const model = useMemo(() => buildActivityDigest(items.filter(isActivityDigestItem)), [items])
    const [expanded, setExpanded] = useState(false)
    const [selectedKey, setSelectedKey] = useState<string | null>(null)

    const summaryItem = model.currentItem ?? model.latestItem
    const summaryItemLabel = summaryItem ? getRenderer(summaryItem.group).getLabel(summaryItem.group) : undefined
    const summary = activitySummaryLabel(model.summary)

    return (
        <div className="py-1" data-activity-digest>
            <button
                type="button"
                className="btn flex w-full cursor-pointer items-center gap-2 border border-border bg-base-200/50 px-3 py-1.5 text-left text-sm text-base-content transition-colors hover:bg-base-300"
                aria-expanded={expanded}
                onClick={() => setExpanded((current) => !current)}
            >
                {model.summary.isPending ? (
                    <Loader size="1em" className="flex-shrink-0 animate-spin text-muted" />
                ) : (
                    <Wrench size="1em" className="flex-shrink-0 text-muted" />
                )}
                <span className="flex-shrink-0 font-medium">{model.summary.isPending ? "Working" : "Activity"}</span>
                <span className="min-w-0 truncate text-xs text-muted">{summaryItemLabel ? `${summaryItemLabel} · ${summary}` : summary}</span>
                {model.summary.failures > 0 && (
                    <span className="ml-auto flex flex-shrink-0 items-center gap-1 text-xs font-medium text-error">
                        <AlertCircle size="1em" />
                        {countLabel(model.summary.failures, "failure", "failures")}
                    </span>
                )}
                {expanded ? <ChevronDown size="1em" className="flex-shrink-0 text-muted" /> : <ChevronRight size="1em" className="flex-shrink-0 text-muted" />}
            </button>

            {expanded && (
                <div className="divide-y divide-border border border-border bg-base-100">
                    {model.items.map((item) => (
                        <ActivityItemRow
                            key={item.id}
                            item={item}
                            selected={selectedKey === item.id}
                            onToggle={() => toggleItem(setSelectedKey, item.id)}
                            commentContext={commentContext}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function ActivityItemRow({
    item,
    selected,
    onToggle,
    commentContext,
}: {
    item: ActivityDigestItem
    selected: boolean
    onToggle: () => void
    commentContext: CommentContext
}) {
    const renderer = getRenderer(item.group)
    const isError = "isError" in item.group && item.group.isError
    const isPending = "isPending" in item.group && item.group.isPending

    return (
        <div>
            <button
                type="button"
                className={cx(
                    "btn flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-base-content transition-colors hover:bg-base-200/60",
                    isError && "bg-error/5"
                )}
                aria-expanded={selected}
                onClick={onToggle}
                title={activityItemTitle(item)}
            >
                {isPending && <Loader size="1em" className="flex-shrink-0 animate-spin text-muted" />}
                {renderer.getStatusIcon?.(item.group)}
                {renderer.getIcon(item.group)}
                <span className={cx("min-w-0 flex-1 truncate font-medium", isError && "text-error")}>{renderer.getLabel(item.group)}</span>
                {renderer.getHeaderInfo?.(item.group)}
                {selected ? <ChevronDown size="1em" className="flex-shrink-0 text-muted" /> : <ChevronRight size="1em" className="flex-shrink-0 text-muted" />}
            </button>
            {selected && <div className="border-t border-border">{renderer.renderContent(item.group, commentContext)}</div>}
        </div>
    )
}

function activityItemTitle(item: ActivityDigestItem): string | undefined {
    switch (item.group.type) {
        case "edit":
        case "write":
        case "fileChange":
            return item.group.filePath
        default:
            return undefined
    }
}

function activitySummaryLabel(summary: ReturnType<typeof buildActivityDigest>["summary"]): string {
    const parts = [
        summary.filesTouched > 0 ? countLabel(summary.filesTouched, "file touched", "files touched") : null,
        summary.commands > 0 ? countLabel(summary.commands, "command", "commands") : null,
        summary.checks > 0 ? countLabel(summary.checks, "check", "checks") : null,
        summary.other > 0 ? `${summary.other} other` : null,
    ].filter((part): part is string => part !== null)
    return parts.join(" · ")
}

function countLabel(count: number, singular: string, plural: string): string {
    return `${count} ${count === 1 ? singular : plural}`
}

function toggleItem(setSelectedKey: (update: (current: string | null) => string | null) => void, key: string): void {
    setSelectedKey((current) => (current === key ? null : key))
}
